// WHIT-482 P4: the hotel page — its address, the offer filters in its URL,
// and the "list, then verify each offer" bookkeeping. Pure (no DOM, no
// React) so `node --test web/utils/packageHotel.test.js` can load it.

const { productPath } = require('./productPaths');

// The page's route; its addresses come from productPaths (WHIT-505).
const HOTEL_PATH = '/package-tours/hotel/';
const HOTEL_ID = /^[0-9A-Za-z_-]{1,20}$/;
const CODE = /^[A-Za-z0-9_-]{1,40}$/;

// How many offers are checked with the tour operator at the same time, as
// the incumbent does; each check is a live provider call.
const VERIFY_CONCURRENCY = 3;
// A check that has not answered by then counts as failed, so a hung provider
// call cannot hold its slot (and the offers queued behind it) for good.
const VERIFY_TIMEOUT_MS = 30000;
const OFFER_PAGE_SIZE = 20;
// allmylespy's largest offer page.
const OFFER_PAGE_MAX = 100;

// Only these are exact enough for a map pin or a distance (api-contract.md,
// operation 4).
const PRECISE_ACCURACY = ['ADDRESS', 'STREET'];

const first = value => (Array.isArray(value) ? value[0] : value);

// "Baia Bodrum Hotel & Spa" → "baia-bodrum-hotel-spa". The slug is for
// people and search engines only; the hotel id after it is what the page
// reads.
const slugify = name =>
  String(name || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80) || 'hotel';

// The hotel page's address in the page's language, ending in a slash
// (trailingSlash is on): /csomagtura/szalloda/<name>/<id>/ for a package,
// /csak-szallas/<name>/<id>/ for hotel only (WHIT-505).
/**
 * @param {string} name
 * @param {string} hotelId
 * @param {string | undefined} locale
 * @param {string | null} [product]  'hotel' for hotel only
 */
const hotelPath = (name, hotelId, locale, product = null) =>
  productPath(`${HOTEL_PATH}${slugify(name)}/${hotelId}/`, locale, product);

const codes = value => {
  const raw = first(value);
  if (!raw) return [];
  return String(raw)
    .split(',')
    .filter(code => CODE.test(code));
};

/**
 * @typedef {{
 *   nightsMin: number | null,
 *   nightsMax: number | null,
 *   airports: string[],
 *   boardTypes: string[],
 *   roomTypes: string[],
 *   roomViews: string[],
 *   sort: string,
 * }} OfferFilters
 */

// The offer filters in the hotel page's URL. They sit beside the search's
// own parameters, prefixed with "o" so neither overwrites the other; a URL
// that names a filter is a filtered page on first load (WHIT-390).
/**
 * @param {Record<string, string | string[] | undefined> | undefined} query
 * @returns {OfferFilters}
 */
const parseOfferFilters = query => {
  const q = query || {};
  const filters = {
    nightsMin: null,
    nightsMax: null,
    airports: codes(q.oair),
    boardTypes: codes(q.oboard),
    roomTypes: codes(q.oroom),
    roomViews: codes(q.oview),
    sort: first(q.osort) === 'date' ? 'date' : 'price',
  };
  const nights = String(first(q.onights) || '').match(/^(\d{1,2})-(\d{1,2})$/);
  if (nights && Number(nights[1]) <= Number(nights[2])) {
    filters.nightsMin = Number(nights[1]);
    filters.nightsMax = Number(nights[2]);
  }
  return filters;
};

/**
 * @param {OfferFilters} filters
 * @returns {Record<string, string>}
 */
const toOfferQuery = filters => {
  const query = {};
  if (filters.nightsMin !== null && filters.nightsMax !== null)
    query.onights = `${filters.nightsMin}-${filters.nightsMax}`;
  if (filters.airports.length) query.oair = filters.airports.join(',');
  if (filters.boardTypes.length) query.oboard = filters.boardTypes.join(',');
  if (filters.roomTypes.length) query.oroom = filters.roomTypes.join(',');
  if (filters.roomViews.length) query.oview = filters.roomViews.join(',');
  if (filters.sort !== 'price') query.osort = filters.sort;
  return query;
};

// The `offerFilters` argument of packageHotelOffers, or null when none is set.
// A hotel-only stay (hotelOnlyHotelOffers, WHIT-484) has no departure
// airport to filter by.
/**
 * @param {OfferFilters} filters
 * @param {boolean} [hotelOnly]
 * @returns {Record<string, unknown> | null}
 */
const toOfferFiltersInput = (filters, hotelOnly = false) => {
  /** @type {Record<string, unknown>} */
  const input = {};
  if (filters.nightsMin !== null && filters.nightsMax !== null)
    input.nights = { min: filters.nightsMin, max: filters.nightsMax };
  if (filters.airports.length && !hotelOnly)
    input.departureAirportIds = filters.airports;
  if (filters.boardTypes.length) input.boardTypes = filters.boardTypes;
  if (filters.roomTypes.length) input.roomTypes = filters.roomTypes;
  if (filters.roomViews.length) input.roomViews = filters.roomViews;
  return Object.keys(input).length ? input : null;
};

// "Earliest first" is by departure for a package, by check-in for a stay.
/**
 * @param {OfferFilters} filters
 * @param {boolean} [hotelOnly]
 */
const offerSortInput = (filters, hotelOnly = false) =>
  filters.sort === 'date'
    ? {
        by: hotelOnly ? 'CHECK_IN_DATE' : 'DEPARTURE_DATE',
        direction: 'ASC',
      }
    : { by: 'PRICE', direction: 'ASC' };

// The nights filter as the Nights select's value: "" for none, "7" for one
// length, "4-7" for a range a link carried (so the filter applied is the one
// shown).
/**
 * @param {OfferFilters} filters
 * @returns {string}
 */
const nightsSelection = filters => {
  if (filters.nightsMin === null || filters.nightsMax === null) return '';
  return filters.nightsMin === filters.nightsMax
    ? String(filters.nightsMin)
    : `${filters.nightsMin}-${filters.nightsMax}`;
};

// The Nights select's value back as the filter's bounds.
/**
 * @param {string} value
 * @returns {{ nightsMin: number | null, nightsMax: number | null }}
 */
const nightsFromSelection = value => {
  const match = String(value || '').match(/^(\d{1,2})(?:-(\d{1,2}))?$/);
  if (!match) return { nightsMin: null, nightsMax: null };
  const min = Number(match[1]);
  const max = match[2] === undefined ? min : Number(match[2]);
  return min <= max
    ? { nightsMin: min, nightsMax: max }
    : { nightsMin: null, nightsMax: null };
};

// WHIT-526: the hotel page's filter panel. A section's choices: the
// offers' values with their counts, and a value from the URL that this
// hotel's offers lack, so the filter the page applies is never hidden.
/**
 * @param {{ id: string | number, name: string, count: number }[] | null | undefined} options
 * @param {string} value  the chosen value, '' for none
 */
const offerChoices = (options, value) => {
  const all = [...(options || [])];
  if (value && !all.some(option => String(option.id) === value))
    all.push({ id: value, name: value, count: 0 });
  return all;
};

// The offer filters in use, one removable tag each, in the panel's order;
// `changes` is what removing it does. The sort is not a filter.
const OFFER_LISTS = ['airports', 'boardTypes', 'roomTypes', 'roomViews'];

/**
 * @param {OfferFilters} filters
 * @returns {{ key: string, value: string, changes: Partial<OfferFilters> }[]}
 */
const activeOfferFilters = filters => {
  const tags = [];
  const nights = nightsSelection(filters);
  if (nights)
    tags.push({
      key: 'nights',
      value: nights,
      changes: { nightsMin: null, nightsMax: null },
    });
  for (const key of OFFER_LISTS)
    for (const value of filters[key] || [])
      tags.push({
        key,
        value,
        changes: { [key]: filters[key].filter(other => other !== value) },
      });
  return tags;
};

// "Clear filters": every offer filter off, the sort kept.
const clearOfferFilters = () => ({
  nightsMin: null,
  nightsMax: null,
  airports: [],
  boardTypes: [],
  roomTypes: [],
  roomViews: [],
});

// "Show more offers" asks for a bigger first page rather than the next one,
// so the offers already checked keep their place.
const offerPageInput = shown => ({
  number: 1,
  size: Math.min(Math.max(shown, OFFER_PAGE_SIZE), OFFER_PAGE_MAX),
});

// What a verification answer means for the offer in the list:
//   available   — OK, and we can take the booking
//   onRequest   — ON_REQUEST: bookable, the operator confirms later
//   notBookable — available, but not online: the office arranges it
//   removed     — gone; the list drops it
//   failed      — the check itself failed; the offer stays, unconfirmed
/**
 * @param {{ status: number, availability?: string | null, bookable?: boolean | null, enquiry?: object | null } | null} answer
 * @returns {'available' | 'onRequest' | 'notBookable' | 'removed' | 'failed'}
 */
const verificationOutcome = answer => {
  // Online booking not connected: the check failed, but the office can
  // still arrange the trip (WHIT-495).
  if (answer && answer.status !== 200 && answer.enquiry) return 'notBookable';
  if (!answer || answer.status !== 200) return 'failed';
  if (answer.availability === 'OK' || answer.availability === 'ON_REQUEST') {
    if (!answer.bookable) return 'notBookable';
    return answer.availability === 'OK' ? 'available' : 'onRequest';
  }
  // UNAVAILABLE, and anything not OK or ON_REQUEST.
  return 'removed';
};

// The offers to start checking now: the first not-yet-checked ones in list
// order, as many as the free slots allow.
/**
 * @param {{ offerId: string }[]} offers
 * @param {Record<string, string>} states  offerId → 'verifying' | outcome
 * @param {number} [concurrency]
 * @returns {string[]}
 */
const nextToVerify = (offers, states, concurrency = VERIFY_CONCURRENCY) => {
  const running = Object.values(states).filter(
    state => state === 'verifying',
  ).length;
  const free = Math.max(concurrency - running, 0);
  return offers
    .map(offer => offer.offerId)
    .filter(id => id && !states[id])
    .slice(0, free);
};

// A pin needs coordinates and an address-level position.
const canPin = geo =>
  !!geo &&
  PRECISE_ACCURACY.includes(geo.accuracy) &&
  typeof geo.lat === 'number' &&
  typeof geo.lon === 'number';

// WHIT-497: the results map. A search result's position is never more than
// "in the area" (APY-1519 sends accuracy UNKNOWN), so the list map pins every
// hotel that has usable coordinates, and makes no distance claim from them.
const onEarth = (lat, lon) =>
  Number.isFinite(lat) &&
  Number.isFinite(lon) &&
  Math.abs(lat) <= 90 &&
  Math.abs(lon) <= 180 &&
  // 0,0 is the sea off Africa: a missing position, not a hotel.
  !(lat === 0 && lon === 0);

/**
 * @param {{ hotelId: string, name: string, geo?: { lat: number | null, lon: number | null } | null, leadIn?: { price?: object | null } | null }[] | null | undefined} results
 */
const mapPins = results =>
  (results || [])
    .filter(hotel => hotel.geo && onEarth(hotel.geo.lat, hotel.geo.lon))
    .map(hotel => ({
      hotelId: hotel.hotelId,
      name: hotel.name,
      lat: hotel.geo.lat,
      lon: hotel.geo.lon,
      price: hotel.leadIn?.price || null,
      // Where its cheapest offer flies from, for its route (WHIT-506).
      airportId: hotel.leadIn?.departureAirportId || null,
      // Its destination region, where the list map's routes end (WHIT-506).
      region: hotel.location?.region || null,
    }));

// The view that shows every pin: [[west, south], [east, north]]. One pin has
// no extent, so the map centres on it instead (null).
/**
 * @param {{ lat: number, lon: number }[]} pins
 * @returns {[[number, number], [number, number]] | null}
 */
const pinBounds = pins => {
  if (pins.length < 2) return null;
  const lats = pins.map(pin => pin.lat);
  // The narrowest span of longitude holding every pin: cut the circle at its
  // widest empty gap. Pins either side of the 180th meridian (Fiji, say) are
  // framed across it, the east edge then lying past 180, which the map
  // accepts.
  const lons = pins.map(pin => pin.lon).sort((a, b) => a - b);
  let west = lons[0];
  let east = lons[lons.length - 1];
  let widestGap = west + 360 - east;
  for (let i = 0; i < lons.length - 1; i += 1) {
    if (lons[i + 1] - lons[i] > widestGap) {
      widestGap = lons[i + 1] - lons[i];
      west = lons[i + 1];
      east = lons[i] + 360;
    }
  }
  /** @type {[[number, number], [number, number]]} */
  const bounds = [
    [west, Math.min(...lats)],
    [east, Math.max(...lats)],
  ];
  return bounds[0][0] === bounds[1][0] && bounds[0][1] === bounds[1][1]
    ? null
    : bounds;
};

// "Not to be confused with … 50 m away" makes a distance claim, so it needs
// the same precision as a pin.
/**
 * @template {{ hotelId: string, name: string }} T
 * @param {T[] | null | undefined} lookalikes
 * @param {{ accuracy: string | null } | null | undefined} geo
 * @returns {T[]}
 */
const lookalikesToShow = (lookalikes, geo) =>
  PRECISE_ACCURACY.includes(geo && geo.accuracy)
    ? (lookalikes || []).filter(other => other && other.hotelId && other.name)
    : [];

// The street address on one line; without a street, the city alone (a
// city-level position claims nothing more precise).
const addressLine = address => {
  if (!address) return '';
  if (!address.street) return address.city || '';
  return [
    address.street,
    [address.postCode, address.city].filter(Boolean).join(' '),
  ]
    .filter(Boolean)
    .join(', ');
};

// The hotel's photos that have a picture in any size (WHIT-505 D1).
const hotelPhotos = images =>
  (images || []).filter(
    image => image && (image.large || image.medium || image.thumbnail),
  );

// One step through the photos, wrapping round at either end.
const stepPhoto = (index, delta, total) =>
  total > 0 ? (((index + delta) % total) + total) % total : 0;

// The photo preview above the hotel page's tabs: one large photo and up to
// four beside it; the last one shown also opens every photo when there are
// more than are shown.
const PREVIEW_THUMBS = 4;
const photoPreview = total => {
  const thumbs = Math.max(0, Math.min(PREVIEW_THUMBS, total - 1));
  return { thumbs, moreOnLast: total > thumbs + 1 };
};

// WHIT-505 B1: the hotel page's route map. Where a flight to the hotel can
// start — every departure airport its offers have — and what each costs.

// A position the map can use, of any accuracy: a route ends "in the area"
// as well as at the door. The hotel's own tag still needs canPin.
/**
 * @param {{ lat: number | null, lon: number | null } | null | undefined} geo
 * @returns {{ lat: number, lon: number } | null}
 */
const mapPoint = geo =>
  geo && onEarth(geo.lat, geo.lon) ? { lat: geo.lat, lon: geo.lon } : null;

// An airport's position from a location search for its IATA code: the first
// result that is that airport and has one (the search also finds places
// whose names merely start with the code).
/**
 * @param {{ iataCode?: string | null, location?: { lat: string | null, lon: string | null } | null }[] | null | undefined} results
 * @param {string} iata
 * @returns {{ lat: number, lon: number } | null}
 */
const airportPoint = (results, iata) => {
  for (const result of results || []) {
    if (!result || result.iataCode !== iata || !result.location) continue;
    const point = mapPoint({
      lat: Number.parseFloat(result.location.lat),
      lon: Number.parseFloat(result.location.lon),
    });
    if (point) return point;
  }
  return null;
};

// The lowest of some prices; one that is missing is skipped.
/**
 * @param {({ amount: number, currency: string } | null | undefined)[] | null | undefined} prices
 * @returns {{ amount: number, currency: string } | null}
 */
const lowestMoney = prices =>
  (prices || []).reduce(
    (
      /** @type {{ amount: number, currency: string } | null} */ lowest,
      price,
    ) => {
      if (!price || typeof price.amount !== 'number') return lowest;
      return !lowest || price.amount < lowest.amount ? price : lowest;
    },
    null,
  );

// The lowest per-person price among offers.
/**
 * @param {{ price?: { perPerson?: { amount: number, currency: string } | null } | null }[] | null | undefined} offers
 * @returns {{ amount: number, currency: string } | null}
 */
const lowestPrice = offers =>
  lowestMoney((offers || []).map(offer => offer?.price?.perPerson));

// One tag per departure airport whose position is known. With no airport
// chosen every route is drawn solid; once one is, it alone is.
/**
 * @param {{ id: string, name: string }[]} airports
 * @param {Record<string, { lat: number, lon: number } | null>} points
 * @param {Record<string, { amount: number, currency: string } | null>} prices
 * @param {string} chosen  the departure-airport filter, '' for none
 */
const routeTags = (airports, points, prices, chosen) =>
  airports
    .filter(airport => points[airport.id])
    .map(airport => ({
      id: airport.id,
      name: airport.name,
      lat: points[airport.id].lat,
      lon: points[airport.id].lon,
      price: prices[airport.id] || null,
      active: !chosen || airport.id === chosen,
    }));

// WHIT-506: an offer row hovered or focused on the hotel page. Only its
// airport's route is drawn, tagged with that offer's own price. An offer
// from an airport the map has no position for leaves the map as it is.
/**
 * @template {{ id: string, price: object | null, active: boolean }} T
 * @param {T[]} tags  routeTags
 * @param {{ airportId: string, price: object | null } | null} focus
 * @returns {T[]}
 */
const focusTags = (tags, focus) => {
  if (!focus) return tags;
  const tag = tags.find(other => other.id === focus.airportId);
  return tag ? [{ ...tag, price: focus.price, active: true }] : tags;
};

// The routes as GeoJSON lines, airport to hotel, each marked active or not
// for the map's solid and dashed layers.
/**
 * @param {{ id: string, lat: number, lon: number, active: boolean }[]} tags
 * @param {{ lat: number, lon: number }} end
 * @returns {import('geojson').FeatureCollection<import('geojson').LineString>}
 */
const routeLines = (tags, end) => ({
  type: 'FeatureCollection',
  features: tags.map(tag => ({
    type: 'Feature',
    properties: { id: tag.id, active: tag.active },
    geometry: {
      type: 'LineString',
      coordinates: [
        [tag.lon, tag.lat],
        [end.lon, end.lat],
      ],
    },
  })),
});

// WHIT-506: which hotels a departure airport serves, and from what price:
// the list search filtered to that airport answers with them (leadIn is the
// hotel's cheapest offer from there).
/**
 * @param {{ hotelId: string, leadIn?: { price?: { amount: number, currency: string } | null } | null }[] | null | undefined} results
 * @returns {Record<string, { amount: number, currency: string } | null>}
 */
const servedHotels = results =>
  Object.fromEntries(
    (results || [])
      .filter(hotel => hotel && hotel.hotelId)
      .map(hotel => [hotel.hotelId, hotel.leadIn?.price || null]),
  );

// WHIT-506: the hotel list's routes, from every departure airport that
// serves the hotels shown (`served`: airport id → servedHotels; null until
// those searches answer, and then each hotel's cheapest-offer airport stands
// in, as it does for a hotel no airport's answer lists). An airport's tag carries the lowest price among the hotels it serves.
// With no card hovered a route runs from each airport to each destination
// region it serves, ending at the middle of that region's hotels: one line
// per hotel would be dozens. With a hotel card hovered or focused (`focus`,
// its hotelId) only that hotel's routes are drawn, one from every airport
// that serves it, each airport tagged with the hotel's price from there.
// Airports without a known position get no route.
/**
 * @param {{ hotelId: string, lat: number, lon: number, price: { amount: number, currency: string } | null, airportId: string | null, region?: string | null }[]} pins
 * @param {Record<string, { lat: number, lon: number } | null>} points  airport id → position
 * @param {string} focus  the hovered hotel's id, '' for none
 * @param {Record<string, Record<string, { amount: number, currency: string } | null>> | null} [served]
 */
const listRoutes = (pins, points, focus, served = null) => {
  // A hotel no airport's answer lists (below an airport's first page, on a
  // later page of the list, or its airport's search failed) keeps its
  // cheapest-offer airport.
  const leadIn = pin =>
    pin.airportId
      ? [
          {
            airportId: /** @type {string} */ (pin.airportId),
            pin,
            price: pin.price,
          },
        ]
      : [];
  const pairs = pins.flatMap(pin => {
    const listed = served
      ? Object.keys(served)
          // Only airports the map can place: a hotel listed by none of those
          // keeps its cheapest-offer airport.
          .filter(
            airportId => pin.hotelId in served[airportId] && points[airportId],
          )
          .map(airportId => ({
            airportId,
            pin,
            price: served[airportId][pin.hotelId],
          }))
      : [];
    return listed.length ? listed : leadIn(pin);
  });
  const shown = pairs.filter(
    pair => points[pair.airportId] && (!focus || pair.pin.hotelId === focus),
  );
  /** @type {Record<string, { id: string, lat: number, lon: number, price: { amount: number, currency: string } | null }>} */
  const airports = {};
  shown.forEach(({ airportId, price }) => {
    const point = /** @type {{ lat: number, lon: number }} */ (
      points[airportId]
    );
    const current = airports[airportId];
    airports[airportId] = {
      id: airportId,
      lat: point.lat,
      lon: point.lon,
      price: lowestMoney([current && current.price, price]),
    };
  });
  // Where a route ends: the hovered hotel, or the middle of a region's
  // hotels (a hotel without a region is a region of its own).
  const regionOf = pin => pin.region || `hotel:${pin.hotelId}`;
  /** @type {Record<string, { lat: number, lon: number }>} */
  const ends = {};
  if (!focus) {
    /** @type {Record<string, typeof pins>} */
    const regions = {};
    pins.forEach(pin => {
      (regions[regionOf(pin)] = regions[regionOf(pin)] || []).push(pin);
    });
    Object.entries(regions).forEach(([region, members]) => {
      ends[region] = {
        lat: members.reduce((sum, pin) => sum + pin.lat, 0) / members.length,
        lon: members.reduce((sum, pin) => sum + pin.lon, 0) / members.length,
      };
    });
  }
  /** @type {Record<string, import('geojson').Feature<import('geojson').LineString>>} */
  const features = {};
  shown.forEach(({ airportId, pin }) => {
    const to = focus ? pin.hotelId : regionOf(pin);
    const end = focus ? pin : ends[to];
    const from = airports[airportId];
    features[`${airportId}>${to}`] = {
      type: 'Feature',
      properties: { id: `${airportId}>${to}`, active: true },
      geometry: {
        type: 'LineString',
        coordinates: [
          [from.lon, from.lat],
          [end.lon, end.lat],
        ],
      },
    };
  });
  return {
    airports: Object.values(airports),
    /** @type {import('geojson').FeatureCollection<import('geojson').LineString>} */
    lines: { type: 'FeatureCollection', features: Object.values(features) },
  };
};

// Airports near each other (Budapest and Debrecen at a zoom that also shows
// Bodrum) would put one tag over the other, leaving it unclickable. Each tag
// that would overlap one already placed is lifted above it; the lowest tags
// are placed first, so the stack grows into the map's north. A tag the stack
// would push past the map's top edge goes below its airport instead (`drop`
// px under the anchor, clear of its dot), stacking downwards, as long as it
// fits there. Positions are screen pixels: x the tag's centre, y its bottom
// edge (its anchor). Returns each tag's lift in pixels; a tag placed below
// has a negative lift.
/**
 * @param {{ id: string, x: number, y: number, width: number, height: number }[]} tags
 * @param {{ gap?: number, drop?: number, top?: number, bottom?: number }} [options]
 *   top and bottom: the map's edges (default: unbounded)
 * @returns {Record<string, number>}
 */
const stackTags = (
  tags,
  { gap = 2, drop = 0, top = -Infinity, bottom = Infinity } = {},
) => {
  /** @type {{ x: number, width: number, top: number, bottom: number }[]} */
  const placed = [];
  /** @type {Record<string, number>} */
  const lifts = {};
  [...tags]
    .sort((a, b) => b.y - a.y || a.x - b.x)
    .forEach(tag => {
      const clash = lift =>
        placed.find(
          other =>
            Math.abs(other.x - tag.x) < (other.width + tag.width) / 2 + gap &&
            tag.y - lift - tag.height < other.bottom + gap &&
            tag.y - lift > other.top - gap,
        );
      let up = 0;
      for (let other = clash(up); other; other = clash(up)) {
        up = tag.y - other.top + gap;
      }
      let lift = up;
      if (tag.y - up - tag.height < top) {
        let down = -(drop + tag.height);
        for (let other = clash(down); other; other = clash(down)) {
          down = tag.y - (other.bottom + gap + tag.height);
        }
        if (tag.y - down <= bottom) lift = down;
      }
      placed.push({
        x: tag.x,
        width: tag.width,
        top: tag.y - lift - tag.height,
        bottom: tag.y - lift,
      });
      lifts[tag.id] = lift;
    });
  return lifts;
};

// A tag centred on an airport near the map's side would hang over the edge
// and be cut off: its centre moves in just enough to keep the whole tag
// inside, `margin` px from either side. Too wide for the map: centred.
/**
 * @param {number} x  the airport's x on screen
 * @param {number} width  the tag's width
 * @param {number} mapWidth
 * @param {number} [margin]
 */
const clampTagX = (x, width, mapWidth, margin = 4) => {
  const low = margin + width / 2;
  const high = mapWidth - margin - width / 2;
  if (low > high) return mapWidth / 2;
  return Math.min(Math.max(x, low), high);
};

// The route map's framing padding: 48 all round, and above the airports
// room for the tags stacked on one another (the airports are usually the
// northern end) — but at most 40% of the map, so the routes keep room to
// show; stackTags puts the tags that do not fit above under the airports.
/**
 * @param {number} tagCount
 * @param {number} tagHeight  a stacked tag's height with its gap
 * @param {number} mapHeight  the height the map is shown at
 */
const routePadding = (tagCount, tagHeight, mapHeight) => ({
  top: Math.min(
    Math.max(48, tagCount * tagHeight + 14),
    Math.floor(mapHeight * 0.4),
  ),
  bottom: 48,
  left: 48,
  right: 48,
});

// A route tag's height (text-xs, py-0.5, 2 px border) and the gap between
// stacked ones.
const ROUTE_TAG_HEIGHT = 24;
const ROUTE_TAG_STEP = ROUTE_TAG_HEIGHT + 2;

// How tall the route map is: its usual height, or taller when that would
// not hold every airport's tag in one stack (plus 96 px for the bottom
// padding and the routes), so the tags fit above and below the airports.
/**
 * @param {number} tagCount
 * @param {number} tagHeight  a stacked tag's height with its gap
 * @param {number} base  the map's usual height
 */
const routeMapHeight = (tagCount, tagHeight, base) =>
  Math.max(base, tagCount * tagHeight + 14 + 96);

module.exports = {
  HOTEL_PATH,
  VERIFY_CONCURRENCY,
  VERIFY_TIMEOUT_MS,
  OFFER_PAGE_SIZE,
  OFFER_PAGE_MAX,
  ROUTE_TAG_HEIGHT,
  ROUTE_TAG_STEP,
  slugify,
  hotelPath,
  parseOfferFilters,
  toOfferQuery,
  toOfferFiltersInput,
  nightsSelection,
  nightsFromSelection,
  offerChoices,
  activeOfferFilters,
  clearOfferFilters,
  offerSortInput,
  offerPageInput,
  verificationOutcome,
  nextToVerify,
  canPin,
  onEarth,
  mapPins,
  pinBounds,
  lookalikesToShow,
  addressLine,
  hotelPhotos,
  stepPhoto,
  photoPreview,
  mapPoint,
  airportPoint,
  lowestMoney,
  lowestPrice,
  routeTags,
  focusTags,
  routeLines,
  servedHotels,
  listRoutes,
  stackTags,
  clampTagX,
  routePadding,
  routeMapHeight,
};
